import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmployeeTestPage from "./EmployeeTestPage";

type CourseItem = {
  id: number;
  category: string;
  name: string;
  instructor: string;
  department: string;
  assigned_user: string;
  status: "published" | "private";
  students: number;
  thumbnail?: string | null;
  video_url?: string | null;
  ppt_url?: string | null;
  pdf_url?: string | null;
  end_date?: string | null;
  category_id?: number | null;
};

type TestItem = {
  id: number;
  title: string;
  topic_name?: string;
  course_name: string;
  assigned_users: string;
  question_count: number;
  pass_mark?: number;
  total_marks?: number;
  start_date?: string;
  end_date?: string;
  status: string;
  questions_json?: string;
};

type LoggedInUser = {
  id?: number;
  username?: string;
  email?: string;
  department?: string;
  role?: "employee" | "admin";
  profileImage?: string;
  profile_image?: string;
};

type ActiveMenu = "dashboard" | "courses" | "tests" | "notifications";

type CourseFilter =
  | "all"
  | "assigned"
  | "optional"
  | "in_progress"
  | "completed";

type NotificationItem = {
  id: string;
  type: "assigned" | "deadline" | "chat" | "test";
  title: string;
  message: string;
  courseName?: string;
  senderId?: number;
  createdAt: string;
  courseId?: number;
  testId?: number;
};

type CourseProgressItem = {
  course_id: number;
  final_progress: number;
  course_completed: boolean;
};

type CourseCategoryGroup = {
  categoryKey: string;
  categoryName: string;
  courses: CourseItem[];
  thumbnailCourse: CourseItem;
  averageProgress: number;
  completedCount: number;
  inProgressCount: number;
};

type ChatUser = {
  id: number;
  username: string;
};

type ChatUnreadCounts = {
  [senderId: string]: number;
};

type LearningActivityItem = {
  date: string;
  count: number;
};

type HeatmapDay = {
  key: string;
  date: string | null;
  count: number;
  isPlaceholder: boolean;
};

type HeatmapMonthLabel = {
  key: string;
  label: string;
  isNewMonth: boolean;
};

function EmployeeDashboardPage() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [tests, setTests] = useState<TestItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(() => {
    const savedMenu = localStorage.getItem("employeeDashboardActiveMenu");

    if (
      savedMenu === "dashboard" ||
      savedMenu === "courses" ||
      savedMenu === "tests" ||
      savedMenu === "notifications"
    ) {
      localStorage.removeItem("employeeDashboardActiveMenu");
      return savedMenu;
    }

    return "dashboard";
  });

  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null>(null);
  const [employeeProfileImage, setEmployeeProfileImage] = useState("");

  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState<CourseFilter>("all");

  const [notificationHistory, setNotificationHistory] = useState<
    Record<string, string>
  >({});

  const [selectedCategoryGroup, setSelectedCategoryGroup] =
    useState<CourseCategoryGroup | null>(null);

  const [readNotificationIds, setReadNotificationIds] = useState<string[]>([]);
  const [autoOpenTestId, setAutoOpenTestId] = useState<number | null>(null);

  const [courseProgressMap, setCourseProgressMap] = useState<
    Record<number, CourseProgressItem>
  >({});

  const [chatUsers, setChatUsers] = useState<ChatUser[]>([]);

  const [chatUnreadCounts, setChatUnreadCounts] =
    useState<ChatUnreadCounts>({});

  const [learningActivities, setLearningActivities] = useState<
    LearningActivityItem[]
  >([]);

  const [learningActivityLoading, setLearningActivityLoading] =
    useState(false);

  const fetchChatUnreadCounts = async (userId: number) => {
    try {
      const response = await fetch(
        `/chat/unread-counts?user_id=${userId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch chat unread counts");
      }

      const data = await response.json();
      setChatUnreadCounts(data);
    } catch (error) {
      console.error("Chat unread counts fetch error:", error);
    }
  };

  const fetchChatUsers = async () => {
    try {
      const response = await fetch("/chat/users");

      if (!response.ok) {
        throw new Error("Failed to fetch chat users");
      }

      const data = await response.json();
      setChatUsers(data);
    } catch (error) {
      console.error("Chat users fetch error:", error);
    }
  };

  const fetchLearningActivities = async (
    userId: number,
    showLoading: boolean = true
  ) => {
    try {
      if (showLoading) {
        setLearningActivityLoading(true);
      }

      const response = await fetch(
        `/course-progress/activity/${userId}?days=365`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch learning activities");
      }

      const data: LearningActivityItem[] = await response.json();

      setLearningActivities(
        Array.isArray(data)
          ? data.map((item) => ({
              date: item.date,
              count: Number(item.count || 0)
            }))
          : []
      );
    } catch (error) {
      console.error("Learning activity fetch error:", error);
      setLearningActivities([]);
    } finally {
      if (showLoading) {
        setLearningActivityLoading(false);
      }
    }
  };

  useEffect(() => {
    const storedUser = sessionStorage.getItem("loggedInUser");

    if (storedUser) {
      const user: LoggedInUser = JSON.parse(storedUser);
      setLoggedInUser(user);

      const imagePath = user.profile_image || user.profileImage || "";

      setEmployeeProfileImage(
        imagePath && imagePath.startsWith("/uploads")
          ? `${imagePath}`
          : imagePath
      );

      if (user.id) {
        fetchChatUnreadCounts(user.id);
        fetchLearningActivities(user.id);
      }
    }

    fetchCourses();
    fetchTests();
    fetchChatUsers();
  }, []);

  useEffect(() => {
    if (!loggedInUser?.id) {
      return;
    }

    const intervalId = window.setInterval(() => {
      fetchChatUnreadCounts(loggedInUser.id!);
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loggedInUser?.id]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchCourses(false);
      fetchTests();

      if (loggedInUser?.id) {
        fetchLearningActivities(loggedInUser.id, false);
      }
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loggedInUser?.id]);

  const fetchCourseProgress = async (
    targetCourses: CourseItem[],
    userId: number
  ) => {
    try {
      const progressResults = await Promise.all(
        targetCourses.map(async (course) => {
          const response = await fetch(
            `/course-progress/${userId}/${course.id}`
          );

          if (!response.ok) {
            return null;
          }

          return await response.json();
        })
      );

      const progressMap: Record<number, CourseProgressItem> = {};

      progressResults.forEach((progress) => {
        if (progress) {
          progressMap[progress.course_id] = progress;
        }
      });

      setCourseProgressMap(progressMap);
    } catch (error) {
      console.error("Course progress fetch error:", error);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await fetch("/tests/");

      if (!response.ok) {
        throw new Error("Failed to fetch tests");
      }

      const data = await response.json();
      setTests(data);
    } catch (error) {
      console.error("Error fetching tests:", error);
    }
  };

  useEffect(() => {
    const storedHistory = localStorage.getItem(
      "employeeNotificationHistory"
    );

    if (storedHistory) {
      setNotificationHistory(JSON.parse(storedHistory));
    }
  }, []);

  useEffect(() => {
    const storedReadIds = localStorage.getItem(
      "employeeReadNotificationIds"
    );

    if (storedReadIds) {
      setReadNotificationIds(JSON.parse(storedReadIds));
    }
  }, []);

  const fetchCourses = async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await fetch("/courses/");

      if (!response.ok) {
        throw new Error("Failed to fetch courses");
      }

      const data = await response.json();
      setCourses(data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const employeeCourses = useMemo(() => {
    const username =
      loggedInUser?.username?.trim().toLowerCase() || "";

    const email =
      loggedInUser?.email?.trim().toLowerCase() || "";

    const department =
      loggedInUser?.department?.trim().toLowerCase() || "";

    if (!username && !email && !department) {
      return [];
    }

    return courses.filter((course) => {
      const isPublished = course.status === "published";

      const assignedUserText = (course.assigned_user || "")
        .trim()
        .toLowerCase();

      const departmentText = (course.department || "")
        .trim()
        .toLowerCase();

      const matchesUser =
        (!!username && assignedUserText.includes(username)) ||
        (!!email && assignedUserText.includes(email));

      const matchesDepartment =
        !!department && departmentText.includes(department);

      return isPublished && (matchesUser || matchesDepartment);
    });
  }, [courses, loggedInUser]);

  useEffect(() => {
    if (loggedInUser?.id && employeeCourses.length > 0) {
      fetchCourseProgress(employeeCourses, loggedInUser.id);
    }
  }, [loggedInUser?.id, employeeCourses]);

  const filteredEmployeeCourses = useMemo(() => {
    const keyword = courseSearchTerm.trim().toLowerCase();

    let filteredCourses = employeeCourses;

    if (courseFilter === "optional") {
      filteredCourses = [];
    }

    if (courseFilter === "in_progress") {
      filteredCourses = employeeCourses.filter((course) => {
        const progress =
          courseProgressMap[course.id]?.final_progress || 0;

        return progress > 0 && progress < 100;
      });
    }

    if (courseFilter === "completed") {
      filteredCourses = employeeCourses.filter((course) => {
        const progress =
          courseProgressMap[course.id]?.final_progress || 0;

        return progress >= 100;
      });
    }

    if (!keyword) {
      return filteredCourses;
    }

    return filteredCourses.filter((course) => {
      const categoryName = course.category.toLowerCase();
      const courseName = course.name.toLowerCase();
      const instructorName = course.instructor.toLowerCase();

      if (activeMenu === "dashboard") {
        return categoryName.includes(keyword);
      }

      if (selectedCategoryGroup) {
        return courseName.includes(keyword);
      }

      return (
        courseName.includes(keyword) ||
        instructorName.includes(keyword) ||
        categoryName.includes(keyword)
      );
    });
  }, [
    employeeCourses,
    courseSearchTerm,
    courseFilter,
    courseProgressMap,
    activeMenu,
    selectedCategoryGroup
  ]);

  const filteredEmployeeCategoryGroups = useMemo<
    CourseCategoryGroup[]
  >(() => {
    const groupMap = new Map<string, CourseItem[]>();

    filteredEmployeeCourses.forEach((course) => {
      const categoryKey = course.category_id
        ? `id-${course.category_id}`
        : `name-${course.category || "未分類"}`;

      const existingCourses = groupMap.get(categoryKey) || [];
      groupMap.set(categoryKey, [...existingCourses, course]);
    });

    return Array.from(groupMap.entries()).map(
      ([categoryKey, groupedCourses]) => {
        const firstCourse = groupedCourses[0];

        const totalProgress = groupedCourses.reduce(
          (sum, course) => {
            return (
              sum +
              (courseProgressMap[course.id]?.final_progress || 0)
            );
          },
          0
        );

        const averageProgress =
          groupedCourses.length > 0
            ? Math.round(totalProgress / groupedCourses.length)
            : 0;

        const completedCount = groupedCourses.filter(
          (course) =>
            (courseProgressMap[course.id]?.final_progress || 0) >=
            100
        ).length;

        const inProgressCount = groupedCourses.filter((course) => {
          const progress =
            courseProgressMap[course.id]?.final_progress || 0;

          return progress > 0 && progress < 100;
        }).length;

        return {
          categoryKey,
          categoryName: firstCourse.category || "未分類",
          courses: groupedCourses,
          thumbnailCourse: firstCourse,
          averageProgress,
          completedCount,
          inProgressCount
        };
      }
    );
  }, [filteredEmployeeCourses, courseProgressMap]);

  const selectedCategoryCourses = useMemo(() => {
    if (!selectedCategoryGroup) {
      return [];
    }

    return selectedCategoryGroup.courses;
  }, [selectedCategoryGroup]);

  useEffect(() => {
    const savedCategoryKey = localStorage.getItem(
      "employeeSelectedCategoryKey"
    );

    if (
      activeMenu === "courses" &&
      savedCategoryKey &&
      !selectedCategoryGroup &&
      filteredEmployeeCategoryGroups.length > 0
    ) {
      const matchedGroup = filteredEmployeeCategoryGroups.find(
        (group) => group.categoryKey === savedCategoryKey
      );

      if (matchedGroup) {
        setSelectedCategoryGroup(matchedGroup);
      }

      localStorage.removeItem("employeeSelectedCategoryKey");
    }
  }, [
    activeMenu,
    selectedCategoryGroup,
    filteredEmployeeCategoryGroups
  ]);

  const employeeTests = useMemo(() => {
    if (!loggedInUser) {
      return [];
    }

    return tests.filter((test) => {
      if (test.status !== "published") {
        return false;
      }

      try {
        const assignedUsers = JSON.parse(test.assigned_users);

        return assignedUsers.some(
          (user: {
            id?: number;
            name?: string;
            email?: string;
          }) => {
            return (
              user.id === loggedInUser.id ||
              user.email === loggedInUser.email ||
              user.name === loggedInUser.username
            );
          }
        );
      } catch {
        return false;
      }
    });
  }, [tests, loggedInUser]);

  const deadlineCourses = useMemo(() => {
    return [...filteredEmployeeCourses]
      .filter(
        (course) =>
          course.end_date && course.end_date.trim() !== ""
      )
      .sort((a, b) => {
        const dateA = new Date(a.end_date || "").getTime();
        const dateB = new Date(b.end_date || "").getTime();

        return dateA - dateB;
      });
  }, [filteredEmployeeCourses]);

  const formatDeadlineDate = (dateText?: string | null) => {
    if (!dateText) {
      return "締切日未設定";
    }

    const date = new Date(dateText);

    if (Number.isNaN(date.getTime())) {
      return "締切日未設定";
    }

    return `${date.getFullYear()}年${
      date.getMonth() + 1
    }月${date.getDate()}日`;
  };

  const handleOpenCourse = (course: CourseItem) => {
    setActiveMenu("courses");
    setCourseSearchTerm(course.name);
  };

  const chatNotifications = useMemo(() => {
    return Object.entries(chatUnreadCounts)
      .filter(([, count]) => count > 0)
      .map(([senderId, count]) => {
        const sender = chatUsers.find(
          (user) => user.id === Number(senderId)
        );

        return {
          id: `chat-${senderId}`,
          type: "chat" as const,
          title: "新しいチャットメッセージ",
          message: `${
            sender?.username || "ユーザー"
          } さんから ${count} 件の新しいメッセージがあります。`,
          senderId: Number(senderId),
          createdAt: new Date().toISOString()
        };
      });
  }, [chatUnreadCounts, chatUsers]);

  const notifications = useMemo(() => {
    const today = new Date();
    const sixtyDaysAgo = new Date();

    sixtyDaysAgo.setDate(today.getDate() - 60);

    const generatedNotifications: NotificationItem[] = [];

    employeeCourses.forEach((course) => {
      const assignedKey = `assigned-${course.id}`;

      const assignedCreatedAt =
        notificationHistory[assignedKey] || today.toISOString();

      if (new Date(assignedCreatedAt) >= sixtyDaysAgo) {
        generatedNotifications.push({
          id: assignedKey,
          type: "assigned",
          title: "新しいコースが割り当てられました",
          message: `${course.name} があなたに割り当てられました。`,
          courseName: course.name,
          courseId: course.id,
          createdAt: assignedCreatedAt
        });
      }

      if (course.end_date) {
        const deadlineDate = new Date(course.end_date);

        const diffDays = Math.ceil(
          (deadlineDate.getTime() - today.getTime()) /
            (1000 * 60 * 60 * 24)
        );

        if (diffDays >= 0 && diffDays <= 7) {
          const deadlineKey = `deadline-${course.id}`;

          const deadlineCreatedAt =
            notificationHistory[deadlineKey] ||
            today.toISOString();

          if (new Date(deadlineCreatedAt) >= sixtyDaysAgo) {
            generatedNotifications.push({
              id: deadlineKey,
              type: "deadline",
              title: "締切が近いコースがあります",
              message: `${
                course.name
              } の締切は ${formatDeadlineDate(
                course.end_date
              )} です。`,
              courseName: course.name,
              createdAt: deadlineCreatedAt
            });
          }
        }
      }
    });

    employeeTests.forEach((test) => {
      const testNotificationSignature = [
        test.id,
        test.title,
        test.course_name,
        test.start_date || "no-start-date",
        test.end_date || "no-end-date",
        test.question_count,
        test.total_marks || 0
      ]
        .map((value) => encodeURIComponent(String(value)))
        .join("-");

      const testKey = `test-${testNotificationSignature}`;

      const testCreatedAt =
        notificationHistory[testKey] || today.toISOString();

      if (new Date(testCreatedAt) >= sixtyDaysAgo) {
        generatedNotifications.push({
          id: testKey,
          type: "test",
          title: "新しいテストが割り当てられました",
          message: `${test.title} があなたに割り当てられました。`,
          courseName: test.course_name,
          testId: test.id,
          createdAt: testCreatedAt
        });
      }
    });

    return generatedNotifications;
  }, [employeeCourses, employeeTests, notificationHistory]);

  const allNotifications = useMemo(() => {
    return [...chatNotifications, ...notifications];
  }, [chatNotifications, notifications]);

  const unreadNotifications = useMemo(() => {
    const unreadCourseNotifications = notifications.filter(
      (notification) =>
        !readNotificationIds.includes(notification.id)
    );

    return [
      ...chatNotifications,
      ...unreadCourseNotifications
    ];
  }, [
    notifications,
    readNotificationIds,
    chatNotifications
  ]);

  useEffect(() => {
    const validTestNotificationIds = new Set(
      notifications
        .filter(
          (notification) => notification.type === "test"
        )
        .map((notification) => notification.id)
    );

    setReadNotificationIds((currentIds) => {
      const cleanedIds = currentIds.filter(
        (notificationId) => {
          if (!notificationId.startsWith("test-")) {
            return true;
          }

          return validTestNotificationIds.has(notificationId);
        }
      );

      const hasChanged =
        cleanedIds.length !== currentIds.length ||
        cleanedIds.some(
          (id, index) => id !== currentIds[index]
        );

      if (!hasChanged) {
        return currentIds;
      }

      localStorage.setItem(
        "employeeReadNotificationIds",
        JSON.stringify(cleanedIds)
      );

      return cleanedIds;
    });
  }, [notifications]);

  const handleMarkAsRead = (notificationId: string) => {
    const updatedReadIds = Array.from(
      new Set([...readNotificationIds, notificationId])
    );

    setReadNotificationIds(updatedReadIds);

    localStorage.setItem(
      "employeeReadNotificationIds",
      JSON.stringify(updatedReadIds)
    );
  };

  const handleMarkAllAsRead = () => {
    const updatedReadIds = notifications.map(
      (notification) => notification.id
    );

    setReadNotificationIds(updatedReadIds);

    localStorage.setItem(
      "employeeReadNotificationIds",
      JSON.stringify(updatedReadIds)
    );
  };

  const handleLogout = () => {
    sessionStorage.removeItem("loggedInUser");
    navigate("/login");
  };

  const totalCourses = employeeCourses.length;

  const completedCourses = employeeCourses.filter(
    (course) =>
      (courseProgressMap[course.id]?.final_progress || 0) >= 100
  ).length;

  const inProgressCourses = employeeCourses.filter((course) => {
    const progress =
      courseProgressMap[course.id]?.final_progress || 0;

    return progress > 0 && progress < 100;
  }).length;

  const formatDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const heatmapDays = useMemo<HeatmapDay[]>(() => {
    const activityMap = new Map<string, number>();

    learningActivities.forEach((activity) => {
      activityMap.set(
        activity.date,
        Number(activity.count || 0)
      );
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 364);

    const result: HeatmapDay[] = [];

    for (
      let placeholderIndex = 0;
      placeholderIndex < startDate.getDay();
      placeholderIndex += 1
    ) {
      result.push({
        key: `placeholder-${placeholderIndex}`,
        date: null,
        count: 0,
        isPlaceholder: true
      });
    }

    for (let dayIndex = 0; dayIndex < 365; dayIndex += 1) {
      const currentDate = new Date(startDate);

      currentDate.setDate(
        startDate.getDate() + dayIndex
      );

      const dateKey = formatDateKey(currentDate);

      result.push({
        key: dateKey,
        date: dateKey,
        count: activityMap.get(dateKey) || 0,
        isPlaceholder: false
      });
    }

    return result;
  }, [learningActivities]);

  const heatmapWeeks = useMemo<HeatmapDay[][]>(() => {
    const weeks: HeatmapDay[][] = [];

    for (
      let index = 0;
      index < heatmapDays.length;
      index += 7
    ) {
      const week = heatmapDays.slice(index, index + 7);

      while (week.length < 7) {
        week.push({
          key: `ending-placeholder-${index}-${week.length}`,
          date: null,
          count: 0,
          isPlaceholder: true
        });
      }

      weeks.push(week);
    }

    return weeks;
  }, [heatmapDays]);

  const heatmapMonthLabels = useMemo<HeatmapMonthLabel[]>(() => {
    let previousMonthKey = "";

    return heatmapWeeks.map((week, weekIndex) => {
      const firstRealDay = week.find(
        (day) => !day.isPlaceholder && day.date
      );

      if (!firstRealDay?.date) {
        return {
          key: `month-label-${weekIndex}`,
          label: "",
          isNewMonth: false
        };
      }

      const date = new Date(
        `${firstRealDay.date}T00:00:00`
      );

      const monthKey =
        `${date.getFullYear()}-${date.getMonth()}`;

      const isNewMonth =
        weekIndex === 0 || monthKey !== previousMonthKey;

      const label = isNewMonth
        ? `${date.getMonth() + 1}月`
        : "";

      previousMonthKey = monthKey;

      return {
        key: `month-label-${weekIndex}`,
        label,
        isNewMonth: weekIndex > 0 && isNewMonth
      };
    });
  }, [heatmapWeeks]);

  const activeLearningDays = useMemo(() => {
    return learningActivities.filter(
      (activity) => Number(activity.count || 0) > 0
    ).length;
  }, [learningActivities]);

  const getHeatmapCellColor = (count: number) => {
    if (count <= 0) {
      return "#e2e8f0";
    }

    if (count === 1) {
      return "#ccfbf1";
    }

    if (count <= 3) {
      return "#5eead4";
    }

    if (count <= 6) {
      return "#14b8a6";
    }

    return "#0f766e";
  };

  const formatHeatmapTooltip = (
    dateText: string,
    count: number
  ) => {
    const date = new Date(`${dateText}T00:00:00`);

    const formattedDate = `${date.getFullYear()}年${
      date.getMonth() + 1
    }月${date.getDate()}日`;

    if (count === 0) {
      return `${formattedDate}：学習アクティビティなし`;
    }

    return `${formattedDate}：${count}件の学習アクティビティ`;
  };

  const displayName =
    loggedInUser?.username || "社員ユーザー";

  const displayDepartment =
    loggedInUser?.department || "社員";

  const getNavStyle = (menu: ActiveMenu) => {
    return activeMenu === menu
      ? activeNavItemStyle
      : navItemStyle;
  };

  const getCourseImageStyle = (
    course: CourseItem
  ): React.CSSProperties => {
    const hasUploadedThumbnail =
      course.thumbnail && course.thumbnail.trim() !== "";

    const courseText =
      `${course.category} ${course.name}`.toLowerCase();

    let defaultImageUrl =
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80";

    if (
      courseText.includes("hr") ||
      courseText.includes("人事") ||
      courseText.includes("diversity")
    ) {
      defaultImageUrl =
        "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=900&q=80";
    }

    if (
      courseText.includes("aws") ||
      courseText.includes("cloud") ||
      courseText.includes("python") ||
      courseText.includes("system")
    ) {
      defaultImageUrl =
        "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80";
    }

    const imageUrl = hasUploadedThumbnail
      ? course.thumbnail
      : defaultImageUrl;

    return {
      height: "180px",
      padding: "16px",
      position: "relative",
      backgroundImage: `linear-gradient(120deg, rgba(15,23,42,0.62), rgba(15,23,42,0.28)), url(${imageUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center"
    };
  };

  if (loading) {
    return (
      <div style={loadingPageStyle}>
        読み込み中...
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <aside style={sidebarStyle}>
        <div>
          <div style={logoAreaStyle}>
            <div style={logoIconStyle}>🎓</div>
            <div style={logoTextStyle}>LearnHub</div>
          </div>

          <div style={menuSectionTitleStyle}>
            LEARNING
          </div>

          <nav style={navStyle}>
            <button
              type="button"
              onClick={() => setActiveMenu("dashboard")}
              style={getNavStyle("dashboard")}
            >
              ▦ ダッシュボード
            </button>

            <button
              type="button"
              onClick={() =>
                navigate("/employee-my-courses")
              }
              style={navItemStyle}
            >
              📖 マイコース
            </button>

            <button
              type="button"
              onClick={() => setActiveMenu("tests")}
              style={getNavStyle("tests")}
            >
              📝 テスト
            </button>

            <button
              type="button"
              onClick={() =>
                setActiveMenu("notifications")
              }
              style={getNavStyle("notifications")}
            >
              🔔 通知
            </button>

            <button
              type="button"
              onClick={() => navigate("/employee-chat")}
              style={chatNavItemStyle}
            >
              <span>💬 チャット</span>

              {Object.values(chatUnreadCounts).reduce(
                (total, count) => total + count,
                0
              ) > 0 && (
                <span style={chatNavBadgeStyle}>
                  {Object.values(chatUnreadCounts).reduce(
                    (total, count) => total + count,
                    0
                  )}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() =>
                navigate("/employee-settings")
              }
              style={navItemStyle}
            >
              ⚙️ 設定
            </button>
          </nav>
        </div>

        <div style={profileBottomAreaStyle}>
          <div style={profileAreaStyle}>
            <div style={avatarStyle}>
              {employeeProfileImage ? (
                <img
                  src={employeeProfileImage}
                  alt="プロフィール"
                  style={avatarImageStyle}
                />
              ) : (
                displayName.charAt(0).toUpperCase()
              )}
            </div>

            <div>
              <div style={profileNameStyle}>
                {displayName}
              </div>

              <div style={profileDeptStyle}>
                {displayDepartment}
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate("/employee-settings")
              }
              style={settingButtonStyle}
            >
              ⚙
            </button>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            style={sidebarLogoutButtonStyle}
          >
            ログアウト
          </button>
        </div>
      </aside>

      <main style={mainStyle}>
        <header style={topHeaderStyle}>
          <div>
            <h1 style={pageTitleStyle}>
              {activeMenu === "dashboard" &&
                "ダッシュボード"}

              {activeMenu === "courses" &&
                "マイコース"}

              {activeMenu === "tests" &&
                "テスト"}

              {activeMenu === "notifications" &&
                "通知"}
            </h1>

            <p style={pageSubtitleStyle}>
              {activeMenu === "dashboard" &&
                "学習状況を確認できます"}

              {activeMenu === "courses" &&
                "受講可能なコースを確認できます"}

              {activeMenu === "tests" &&
                "割り当てられたテストを確認できます"}

              {activeMenu === "notifications" &&
                "お知らせを確認できます"}
            </p>
          </div>

          <div style={topRightStyle}>
            <div style={searchBoxStyle}>
              <span style={{ color: "#64748b" }}>
                🔍
              </span>

              <input
                type="text"
                value={courseSearchTerm}
                onChange={(event) =>
                  setCourseSearchTerm(event.target.value)
                }
                placeholder="コース名・講師名・カテゴリーで検索"
                style={searchInputStyle}
              />
            </div>

            <button
              type="button"
              onClick={() =>
                setActiveMenu("notifications")
              }
              style={notificationButtonStyle}
            >
              🔔

              {unreadNotifications.length > 0 && (
                <span style={notificationBadgeStyle}>
                  {unreadNotifications.length}
                </span>
              )}
            </button>
          </div>
        </header>

        {activeMenu === "dashboard" && (
          <>
            <section style={heroStyle}>
              <div>
                <div style={dateTextStyle}>
                  ✦ 2026年 5月 13日
                </div>

                <h2 style={heroTitleStyle}>
                  こんにちは、{displayName}さん！
                </h2>

                <p style={heroTextStyle}>
                  学習を続けましょう。現在公開中のコースがあります。
                </p>

                <div style={heroButtonAreaStyle}>
                  <button
                    type="button"
                    onClick={() => {
                      if (loggedInUser?.id) {
                        const savedCourseId = Number(
                          localStorage.getItem(
                            `employeeLastLearningCourseId_${loggedInUser.id}`
                          )
                        );

                        const lastLearningCourse =
                          employeeCourses.find(
                            (course) =>
                              course.id === savedCourseId
                          );

                        if (lastLearningCourse) {
                          navigate(
                            `/employee-courses/${lastLearningCourse.id}`
                          );

                          return;
                        }
                      }

                      navigate("/employee-my-courses");
                    }}
                    style={heroPrimaryButtonStyle}
                  >
                    学習を続ける
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      navigate("/employee-my-courses")
                    }
                    style={heroSecondaryButtonStyle}
                  >
                    すべてのコースを見る
                  </button>
                </div>
              </div>

              <div style={heroIconWrapStyle}>
                <div style={heroIconOuterStyle}>
                  <div style={heroIconInnerStyle}>
                    ✦
                  </div>
                </div>
              </div>
            </section>

            <section style={statsGridStyle}>
              <div style={statCardStyle}>
                <div>
                  <div style={statLabelStyle}>
                    総コース数
                  </div>

                  <div style={statValueStyle}>
                    {totalCourses}
                  </div>
                </div>

                <div style={statIconStyle}>
                  📖
                </div>
              </div>

              <div style={statCardHighlightStyle}>
                <div>
                  <div style={statLabelStyle}>
                    完了
                  </div>

                  <div style={statValueStyle}>
                    {completedCourses}
                  </div>
                </div>

                <div style={statIconGreenStyle}>
                  ↗
                </div>
              </div>

              <div style={statCardBlueStyle}>
                <div>
                  <div style={statLabelStyle}>
                    進行中
                  </div>

                  <div style={statValueStyle}>
                    {inProgressCourses}
                  </div>
                </div>

                <div style={statIconGreenStyle}>
                  ⏱
                </div>
              </div>
            </section>

            <section style={heatmapCardStyle}>
              <div style={heatmapHeaderStyle}>
                <div>
                  <h2 style={heatmapTitleStyle}>
                    学習アクティビティ
                  </h2>

                  <p style={heatmapDescriptionStyle}>
                    過去365日間のコース学習状況を確認できます。
                  </p>
                </div>

                <div style={heatmapSummaryStyle}>
                  <div style={heatmapSummaryItemStyle}>
                    <span style={heatmapSummaryLabelStyle}>
                      学習日数
                    </span>

                    <strong style={heatmapSummaryValueStyle}>
                      {activeLearningDays}日
                    </strong>
                  </div>
                </div>
              </div>

              {learningActivityLoading ? (
                <div style={heatmapLoadingStyle}>
                  学習アクティビティを読み込み中です...
                </div>
              ) : (
                <>
                  <div style={heatmapScrollableAreaStyle}>
                    <div style={heatmapChartStyle}>
                      <div style={heatmapMonthRowStyle}>
                        <div style={heatmapMonthSpacerStyle} />

                        <div style={heatmapMonthWeeksStyle}>
                          {heatmapMonthLabels.map(
                            (monthItem) => (
                              <div
                                key={monthItem.key}
                                style={{
                                  ...heatmapMonthWeekStyle,
                                  marginLeft:
                                    monthItem.isNewMonth
                                      ? "10px"
                                      : "0"
                                }}
                              >
                                {monthItem.label}
                              </div>
                            )
                          )}
                        </div>
                      </div>

                      <div style={heatmapContentStyle}>
                        <div style={heatmapWeekdayLabelsStyle}>
                          <span />
                          <span>月</span>
                          <span />
                          <span>水</span>
                          <span />
                          <span>金</span>
                          <span />
                        </div>

                        <div style={heatmapWeeksStyle}>
                          {heatmapWeeks.map(
                            (week, weekIndex) => {
                              const firstRealDay =
                                week.find(
                                  (day) =>
                                    !day.isPlaceholder &&
                                    day.date
                                );

                              const currentMonth =
                                firstRealDay?.date
                                  ? new Date(
                                      `${firstRealDay.date}T00:00:00`
                                    ).getMonth()
                                  : null;

                              const previousWeek =
                                heatmapWeeks[
                                  weekIndex - 1
                                ];

                              const previousRealDay =
                                previousWeek?.find(
                                  (day) =>
                                    !day.isPlaceholder &&
                                    day.date
                                );

                              const previousMonth =
                                previousRealDay?.date
                                  ? new Date(
                                      `${previousRealDay.date}T00:00:00`
                                    ).getMonth()
                                  : null;

                              const isNewMonth =
                                weekIndex > 0 &&
                                currentMonth !== null &&
                                previousMonth !== null &&
                                currentMonth !== previousMonth;

                              return (
                                <div
                                  key={`week-${weekIndex}`}
                                  style={{
                                    ...heatmapWeekColumnStyle,
                                    marginLeft: isNewMonth
                                      ? "10px"
                                      : "0"
                                  }}
                                >
                                  {week.map((day) => {
                                    if (
                                      day.isPlaceholder ||
                                      !day.date
                                    ) {
                                      return (
                                        <div
                                          key={day.key}
                                          style={
                                            heatmapPlaceholderStyle
                                          }
                                        />
                                      );
                                    }

                                    return (
                                      <div
                                        key={day.key}
                                        title={formatHeatmapTooltip(
                                          day.date,
                                          day.count
                                        )}
                                        style={{
                                          ...heatmapCellStyle,
                                          backgroundColor:
                                            getHeatmapCellColor(
                                              day.count
                                            )
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={heatmapFooterStyle}>
                    <span>
                      {activeLearningDays === 0
                        ? "まだ学習アクティビティはありません。コースを開始すると記録されます。"
                        : "各マスにカーソルを合わせると、その日の学習回数を確認できます。"}
                    </span>

                    <div style={heatmapLegendStyle}>
                      <span>少ない</span>

                      <span
                        style={{
                          ...heatmapLegendCellStyle,
                          backgroundColor: "#e2e8f0"
                        }}
                      />

                      <span
                        style={{
                          ...heatmapLegendCellStyle,
                          backgroundColor: "#ccfbf1"
                        }}
                      />

                      <span
                        style={{
                          ...heatmapLegendCellStyle,
                          backgroundColor: "#5eead4"
                        }}
                      />

                      <span
                        style={{
                          ...heatmapLegendCellStyle,
                          backgroundColor: "#14b8a6"
                        }}
                      />

                      <span
                        style={{
                          ...heatmapLegendCellStyle,
                          backgroundColor: "#0f766e"
                        }}
                      />

                      <span>多い</span>
                    </div>
                  </div>
                </>
              )}
            </section>
          </>
        )}

        {(activeMenu === "dashboard" ||
          activeMenu === "courses") && (
          <div style={contentGridStyle}>
            <section>
              <div style={sectionHeaderStyle}>
                <h2 style={sectionTitleStyle}>
                  学習を続ける
                </h2>

                <button
                  type="button"
                  onClick={() =>
                    setActiveMenu("courses")
                  }
                  style={viewAllButtonStyle}
                >
                  すべて表示
                </button>
              </div>

              <div style={courseFilterTabsStyle}>
                <button
                  type="button"
                  onClick={() =>
                    setCourseFilter("all")
                  }
                  style={
                    courseFilter === "all"
                      ? activeFilterTabStyle
                      : filterTabStyle
                  }
                >
                  すべて
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setCourseFilter("assigned")
                  }
                  style={
                    courseFilter === "assigned"
                      ? activeFilterTabStyle
                      : filterTabStyle
                  }
                >
                  割り当て済み
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setCourseFilter("optional")
                  }
                  style={
                    courseFilter === "optional"
                      ? activeFilterTabStyle
                      : filterTabStyle
                  }
                >
                  任意
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setCourseFilter("in_progress")
                  }
                  style={
                    courseFilter === "in_progress"
                      ? activeFilterTabStyle
                      : filterTabStyle
                  }
                >
                  進行中
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setCourseFilter("completed")
                  }
                  style={
                    courseFilter === "completed"
                      ? activeFilterTabStyle
                      : filterTabStyle
                  }
                >
                  完了
                </button>
              </div>

              <div style={courseGridStyle}>
                {filteredEmployeeCategoryGroups.length ===
                  0 && (
                  <div style={emptyCourseStyle}>
                    現在、あなたに割り当てられているコースはありません
                  </div>
                )}

                {selectedCategoryGroup && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      marginBottom: "10px"
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedCategoryGroup(null)
                      }
                      style={filterTabStyle}
                    >
                      ← カテゴリー一覧へ戻る
                    </button>

                    <h3
                      style={{
                        marginTop: "18px",
                        fontSize: "22px",
                        fontWeight: 900
                      }}
                    >
                      {selectedCategoryGroup.categoryName}{" "}
                      のコース一覧
                    </h3>
                  </div>
                )}

                {!selectedCategoryGroup &&
                  filteredEmployeeCategoryGroups.map(
                    (group) => {
                      const firstCourse =
                        group.thumbnailCourse;

                      return (
                        <div
                          key={group.categoryKey}
                          style={courseCardStyle}
                        >
                          <div
                            style={getCourseImageStyle(
                              firstCourse
                            )}
                          >
                            <span style={courseTagStyle}>
                              {group.categoryName}
                            </span>

                            <span style={requiredTagStyle}>
                              {group.courses.length} コース
                            </span>

                            <span style={timeTagStyle}>
                              平均 {group.averageProgress}%
                            </span>
                          </div>

                          <div style={courseBodyStyle}>
                            <h3 style={courseTitleStyle}>
                              {group.categoryName}
                            </h3>

                            <p style={courseDescStyle}>
                              このカテゴリーには{" "}
                              {group.courses.length}{" "}
                              件のコースがあります。
                            </p>

                            <div style={courseStatusRowStyle}>
                              <span>
                                完了：
                                {group.completedCount}件
                              </span>

                              <span
                                style={{
                                  color: "#0f766e"
                                }}
                              >
                                進行中：
                                {group.inProgressCount}件
                              </span>
                            </div>

                            <div style={progressBarBgStyle}>
                              <div
                                style={{
                                  ...progressBarStyle,
                                  width: `${group.averageProgress}%`
                                }}
                              />
                            </div>

                            <div style={progressTextStyle}>
                              平均進捗{" "}
                              {group.averageProgress}% 完了
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                localStorage.setItem(
                                  "employeeSelectedCategoryKey",
                                  group.categoryKey
                                );

                                navigate(
                                  "/employee-my-courses"
                                );
                              }}
                              style={courseButtonStyle}
                            >
                              このカテゴリーを見る
                            </button>
                          </div>
                        </div>
                      );
                    }
                  )}

                {selectedCategoryGroup &&
                  selectedCategoryCourses.map(
                    (course) => {
                      const courseProgress =
                        Math.round(
                          courseProgressMap[course.id]
                            ?.final_progress || 0
                        );

                      const courseStatus =
                        courseProgress >= 100
                          ? "完了"
                          : courseProgress > 0
                          ? "進行中"
                          : "未開始";

                      return (
                        <div
                          key={course.id}
                          style={courseCardStyle}
                        >
                          <div
                            style={getCourseImageStyle(
                              course
                            )}
                          >
                            <span style={courseTagStyle}>
                              {course.category ||
                                "コース"}
                            </span>

                            <span style={requiredTagStyle}>
                              公開
                            </span>

                            <span style={timeTagStyle}>
                              {courseStatus}
                            </span>
                          </div>

                          <div style={courseBodyStyle}>
                            <h3 style={courseTitleStyle}>
                              {course.name}
                            </h3>

                            <p style={courseDescStyle}>
                              講師：
                              {course.instructor || "-"}
                            </p>

                            <div style={courseStatusRowStyle}>
                              <span>
                                状態：{courseStatus}
                              </span>

                              <span
                                style={{
                                  color: "#0f766e"
                                }}
                              >
                                受講可能
                              </span>
                            </div>

                            <div style={progressBarBgStyle}>
                              <div
                                style={{
                                  ...progressBarStyle,
                                  width: `${courseProgress}%`
                                }}
                              />
                            </div>

                            <div style={progressTextStyle}>
                              {courseProgress}% 完了
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                localStorage.setItem(
                                  "employeeDashboardActiveMenu",
                                  "courses"
                                );

                                localStorage.setItem(
                                  "employeeSelectedCategoryKey",
                                  selectedCategoryGroup?.categoryKey ||
                                    ""
                                );

                                navigate(
                                  `/employee-courses/${course.id}`
                                );
                              }}
                              style={courseButtonStyle}
                            >
                              {courseProgress > 0
                                ? "学習を続ける"
                                : "学習を開始する"}
                            </button>
                          </div>
                        </div>
                      );
                    }
                  )}
              </div>
            </section>

            <aside>
              <h2 style={sectionTitleStyle}>
                締切予定
              </h2>

              <div style={deadlineListStyle}>
                {deadlineCourses.length === 0 && (
                  <div style={emptyCourseStyle}>
                    締切予定のコースはありません
                  </div>
                )}

                {deadlineCourses
                  .slice(0, 4)
                  .map((course, index) => (
                    <div
                      key={course.id}
                      style={deadlineCardStyle}
                    >
                      <div
                        style={
                          index % 2 === 0
                            ? deadlineThumbOneStyle
                            : deadlineThumbTwoStyle
                        }
                      />

                      <div style={{ flex: 1 }}>
                        <div style={deadlineBadgeStyle}>
                          {course.category ||
                            "コース"}
                        </div>

                        <div style={deadlineTextStyle}>
                          {course.name}
                        </div>

                        <div
                          style={deadlineDateTextStyle}
                        >
                          締切：
                          {formatDeadlineDate(
                            course.end_date
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          handleOpenCourse(course)
                        }
                        style={deadlineArrowButtonStyle}
                      >
                        →
                      </button>
                    </div>
                  ))}
              </div>
            </aside>
          </div>
        )}

        {activeMenu === "tests" && (
          <EmployeeTestPage
            employeeTests={employeeTests}
            autoOpenTestId={autoOpenTestId}
            onAutoOpenHandled={() =>
              setAutoOpenTestId(null)
            }
          />
        )}

        {activeMenu === "notifications" && (
          <div style={notificationPageStyle}>
            <div style={notificationTopBarStyle}>
              <div style={unreadBadgeStyle}>
                {unreadNotifications.length} 件未読
              </div>

              <button
                type="button"
                onClick={handleMarkAllAsRead}
                style={markAllReadButtonStyle}
              >
                ✓ すべて既読にする
              </button>
            </div>

            {allNotifications.length === 0 && (
              <div style={emptySectionCardStyle}>
                現在、新しい通知はありません。
              </div>
            )}

            {[...allNotifications]
              .sort((a, b) => {
                const dateDiff =
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime();

                if (dateDiff !== 0) {
                  return dateDiff;
                }

                const aId =
                  Number(a.id.split("-").pop()) || 0;

                const bId =
                  Number(b.id.split("-").pop()) || 0;

                return bId - aId;
              })
              .map((notification) => (
                <div
                  key={notification.id}
                  style={
                    readNotificationIds.includes(
                      notification.id
                    )
                      ? notificationReadCardStyle
                      : notificationLargeCardStyle
                  }
                >
                  <div style={notificationIconBoxStyle}>
                    {notification.type === "chat"
                      ? "💬"
                      : notification.type ===
                        "deadline"
                      ? "⏰"
                      : notification.type === "test"
                      ? "📝"
                      : "📖"}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={notificationTitleStyle}>
                      {notification.title}
                    </div>

                    <div style={notificationMessageStyle}>
                      {notification.message}
                    </div>

                    <div style={notificationDateStyle}>
                      自動通知
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (
                        notification.type !== "chat"
                      ) {
                        handleMarkAsRead(
                          notification.id
                        );
                      }

                      if (
                        notification.type === "chat"
                      ) {
                        navigate("/employee-chat");
                        return;
                      }

                      if (
                        notification.type === "test" &&
                        notification.testId
                      ) {
                        setAutoOpenTestId(
                          notification.testId
                        );

                        setActiveMenu("tests");
                        return;
                      }

                      if (notification.courseId) {
                        navigate(
                          `/employee-courses/${notification.courseId}`
                        );

                        return;
                      }

                      setActiveMenu("courses");
                    }}
                    style={notificationOpenButtonStyle}
                  >
                    {notification.type === "chat"
                      ? "チャットを開く"
                      : notification.type === "test"
                      ? "テストを確認する"
                      : "コースを確認する"}
                  </button>

                  {notification.type !== "chat" &&
                    !readNotificationIds.includes(
                      notification.id
                    ) && (
                      <button
                        type="button"
                        onClick={() =>
                          handleMarkAsRead(
                            notification.id
                          )
                        }
                        style={markReadButtonStyle}
                      >
                        既読にする
                      </button>
                    )}
                </div>
              ))}
          </div>
        )}
      </main>
    </div>
  );
}

const loadingPageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "22px",
  fontWeight: 700,
  backgroundColor: "#f8fafc"
};

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#f8fafc",
  color: "#0f172a"
};

const sidebarStyle: React.CSSProperties = {
  width: "290px",
  minHeight: "100vh",
  backgroundColor: "#14213d",
  color: "#ffffff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flexShrink: 0
};

const logoAreaStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  padding: "22px",
  borderBottom: "1px solid rgba(255,255,255,0.12)"
};

const logoIconStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  backgroundColor: "#2dd4bf",
  color: "#0f172a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px"
};

const logoTextStyle: React.CSSProperties = {
  fontSize: "20px",
  fontWeight: 800
};

const menuSectionTitleStyle: React.CSSProperties = {
  padding: "22px 28px 10px",
  color: "#94a3b8",
  fontSize: "13px",
  fontWeight: 800
};

const navStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  padding: "0 12px"
};

const navItemStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  backgroundColor: "transparent",
  color: "#cbd5e1",
  textAlign: "left",
  padding: "14px 16px",
  borderRadius: "14px",
  fontSize: "16px",
  fontWeight: 700,
  cursor: "pointer"
};

const activeNavItemStyle: React.CSSProperties = {
  ...navItemStyle,
  backgroundColor: "#1e3a68",
  color: "#ffffff"
};

const profileAreaStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "18px"
};

const avatarStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "50%",
  backgroundColor: "#dbeafe",
  color: "#1e3a8a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  overflow: "hidden",
  flexShrink: 0
};

const profileNameStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 800
};

const profileDeptStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "#93c5fd"
};

const settingButtonStyle: React.CSSProperties = {
  marginLeft: "auto",
  color: "#cbd5e1",
  border: "none",
  backgroundColor: "transparent",
  fontSize: "18px",
  cursor: "pointer"
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  padding: "22px 28px 40px",
  minWidth: 0
};

const topHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "28px"
};

const pageTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "26px",
  fontWeight: 800
};

const pageSubtitleStyle: React.CSSProperties = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: "16px"
};

const topRightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "18px"
};

const searchBoxStyle: React.CSSProperties = {
  width: "340px",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "12px 16px",
  color: "#64748b",
  fontSize: "15px",
  display: "flex",
  alignItems: "center",
  gap: "8px"
};

const searchInputStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: "15px",
  color: "#0f172a",
  backgroundColor: "transparent"
};

const notificationBadgeStyle: React.CSSProperties = {
  position: "absolute",
  top: "-10px",
  right: "-10px",
  width: "22px",
  height: "22px",
  borderRadius: "50%",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const heroStyle: React.CSSProperties = {
  minHeight: "245px",
  borderRadius: "18px",
  padding: "40px",
  background:
    "linear-gradient(120deg, #16295c 0%, #1f3f7a 48%, #0f766e 100%)",
  color: "#ffffff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  overflow: "hidden",
  marginBottom: "36px"
};

const dateTextStyle: React.CSSProperties = {
  fontSize: "16px",
  color: "#ccfbf1",
  fontWeight: 800,
  marginBottom: "14px"
};

const heroTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 900
};

const heroTextStyle: React.CSSProperties = {
  marginTop: "18px",
  color: "#dbeafe",
  fontSize: "18px",
  maxWidth: "650px",
  lineHeight: 1.7
};

const heroButtonAreaStyle: React.CSSProperties = {
  display: "flex",
  gap: "18px",
  marginTop: "26px"
};

const heroPrimaryButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#2dd4bf",
  color: "#0f172a",
  padding: "15px 34px",
  borderRadius: "14px",
  fontSize: "16px",
  fontWeight: 800,
  cursor: "pointer"
};

const heroSecondaryButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "transparent",
  color: "#ffffff",
  padding: "15px 20px",
  borderRadius: "14px",
  fontSize: "16px",
  fontWeight: 800,
  cursor: "pointer"
};

const heroIconWrapStyle: React.CSSProperties = {
  width: "190px",
  height: "190px",
  borderRadius: "50%",
  backgroundColor: "rgba(45, 212, 191, 0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const heroIconOuterStyle: React.CSSProperties = {
  width: "135px",
  height: "135px",
  borderRadius: "50%",
  backgroundColor: "rgba(45, 212, 191, 0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const heroIconInnerStyle: React.CSSProperties = {
  width: "92px",
  height: "92px",
  borderRadius: "50%",
  backgroundColor: "#2dd4bf",
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "42px"
};

const statsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "18px",
  marginBottom: "28px"
};

const statCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const statCardHighlightStyle: React.CSSProperties = {
  ...statCardStyle,
  backgroundColor: "#ecfdf5",
  border: "1px solid #99f6e4"
};

const statCardBlueStyle: React.CSSProperties = {
  ...statCardStyle,
  backgroundColor: "#f0fdfa",
  border: "1px solid #99f6e4"
};

const statLabelStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: "15px",
  fontWeight: 800
};

const statValueStyle: React.CSSProperties = {
  marginTop: "12px",
  fontSize: "34px",
  fontWeight: 900
};

const statIconStyle: React.CSSProperties = {
  width: "54px",
  height: "54px",
  borderRadius: "14px",
  backgroundColor: "#f1f5f9",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "24px"
};

const statIconGreenStyle: React.CSSProperties = {
  ...statIconStyle,
  backgroundColor: "#ccfbf1",
  color: "#0f766e"
};

const heatmapCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "24px",
  marginBottom: "38px",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)"
};

const heatmapHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "24px",
  marginBottom: "22px"
};

const heatmapTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 900,
  color: "#0f172a"
};

const heatmapDescriptionStyle: React.CSSProperties = {
  margin: "8px 0 0",
  color: "#64748b",
  fontSize: "14px"
};

const heatmapSummaryStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap"
};

const heatmapSummaryItemStyle: React.CSSProperties = {
  backgroundColor: "#f0fdfa",
  border: "1px solid #99f6e4",
  borderRadius: "12px",
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  gap: "3px",
  minWidth: "110px"
};

const heatmapSummaryLabelStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: "11px",
  fontWeight: 800
};

const heatmapSummaryValueStyle: React.CSSProperties = {
  color: "#0f766e",
  fontSize: "17px",
  fontWeight: 900
};

const heatmapScrollableAreaStyle: React.CSSProperties = {
  width: "100%",
  overflowX: "auto",
  paddingBottom: "8px"
};

const heatmapChartStyle: React.CSSProperties = {
  minWidth: "1030px"
};

const heatmapMonthRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-end",
  gap: "8px",
  marginBottom: "8px"
};

const heatmapMonthSpacerStyle: React.CSSProperties = {
  width: "22px",
  flexShrink: 0
};

const heatmapMonthWeeksStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-end",
  gap: "4px"
};

const heatmapMonthWeekStyle: React.CSSProperties = {
  width: "13px",
  minWidth: "13px",
  height: "14px",
  color: "#64748b",
  fontSize: "11px",
  lineHeight: "14px",
  whiteSpace: "nowrap",
  overflow: "visible",
  flexShrink: 0
};

const heatmapContentStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px"
};

const heatmapWeekdayLabelsStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateRows: "repeat(7, 13px)",
  gap: "4px",
  width: "22px",
  color: "#64748b",
  fontSize: "9px",
  lineHeight: "13px",
  flexShrink: 0
};

const heatmapWeeksStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "4px"
};

const heatmapWeekColumnStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateRows: "repeat(7, 13px)",
  gap: "4px",
  flexShrink: 0
};

const heatmapCellStyle: React.CSSProperties = {
  width: "13px",
  height: "13px",
  borderRadius: "3px",
  border: "1px solid rgba(15, 23, 42, 0.04)",
  cursor: "default",
  boxSizing: "border-box"
};

const heatmapPlaceholderStyle: React.CSSProperties = {
  width: "13px",
  height: "13px",
  visibility: "hidden",
  boxSizing: "border-box"
};

const heatmapFooterStyle: React.CSSProperties = {
  marginTop: "16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "20px",
  color: "#64748b",
  fontSize: "12px",
  flexWrap: "wrap"
};

const heatmapLegendStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  whiteSpace: "nowrap"
};

const heatmapLegendCellStyle: React.CSSProperties = {
  width: "13px",
  height: "13px",
  borderRadius: "3px",
  border: "1px solid rgba(15, 23, 42, 0.04)"
};

const heatmapLoadingStyle: React.CSSProperties = {
  padding: "42px 20px",
  textAlign: "center",
  color: "#64748b",
  fontSize: "14px",
  fontWeight: 700
};

const contentGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 320px",
  gap: "30px"
};

const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "18px"
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 900
};

const viewAllButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "transparent",
  color: "#0f172a",
  fontWeight: 800,
  cursor: "pointer"
};

const courseGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "20px"
};

const courseCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  overflow: "hidden"
};

const emptyCourseStyle: React.CSSProperties = {
  padding: "40px",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  border: "1px dashed #cbd5e1",
  textAlign: "center",
  color: "#64748b",
  fontWeight: 700
};

const courseTagStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "7px 14px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 800
};

const requiredTagStyle: React.CSSProperties = {
  position: "absolute",
  top: "18px",
  right: "18px",
  color: "#ffffff",
  fontWeight: 800
};

const timeTagStyle: React.CSSProperties = {
  position: "absolute",
  left: "18px",
  bottom: "18px",
  color: "#ffffff",
  fontWeight: 800
};

const courseBodyStyle: React.CSSProperties = {
  padding: "18px"
};

const courseTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 900
};

const courseDescStyle: React.CSSProperties = {
  color: "#64748b",
  lineHeight: 1.6,
  minHeight: "52px"
};

const courseStatusRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: "14px",
  fontWeight: 800,
  color: "#64748b",
  marginBottom: "12px"
};

const progressBarBgStyle: React.CSSProperties = {
  height: "9px",
  borderRadius: "999px",
  backgroundColor: "#dbeafe",
  overflow: "hidden"
};

const progressBarStyle: React.CSSProperties = {
  height: "100%",
  backgroundColor: "#2563eb"
};

const progressTextStyle: React.CSSProperties = {
  marginTop: "8px",
  color: "#64748b",
  fontSize: "14px"
};

const courseButtonStyle: React.CSSProperties = {
  width: "100%",
  marginTop: "12px",
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "13px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer"
};

const deadlineListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  marginTop: "20px"
};

const deadlineCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "16px",
  display: "flex",
  alignItems: "center",
  gap: "14px"
};

const deadlineThumbOneStyle: React.CSSProperties = {
  width: "64px",
  height: "64px",
  borderRadius: "14px",
  background: "linear-gradient(45deg, #1e3a8a, #14b8a6)"
};

const deadlineThumbTwoStyle: React.CSSProperties = {
  ...deadlineThumbOneStyle,
  background: "linear-gradient(45deg, #94a3b8, #e2e8f0)"
};

const deadlineBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  backgroundColor: "#dbeafe",
  color: "#2563eb",
  padding: "5px 12px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 800
};

const deadlineTextStyle: React.CSSProperties = {
  marginTop: "6px",
  color: "#64748b",
  fontSize: "13px"
};

const emptySectionCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "40px",
  color: "#64748b",
  fontSize: "18px",
  fontWeight: 700
};

const deadlineDateTextStyle: React.CSSProperties = {
  marginTop: "6px",
  color: "#ef4444",
  fontSize: "12px",
  fontWeight: 800
};

const deadlineArrowButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "transparent",
  color: "#0f766e",
  fontSize: "22px",
  fontWeight: 900,
  cursor: "pointer"
};

const notificationButtonStyle: React.CSSProperties = {
  position: "relative",
  border: "none",
  backgroundColor: "transparent",
  fontSize: "22px",
  cursor: "pointer"
};

const notificationTitleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: "8px"
};

const notificationMessageStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: "15px",
  marginBottom: "14px"
};

const notificationOpenButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer"
};

const profileBottomAreaStyle: React.CSSProperties = {
  borderTop: "1px solid rgba(255,255,255,0.12)",
  padding: "0 18px 18px"
};

const sidebarLogoutButtonStyle: React.CSSProperties = {
  width: "100%",
  marginTop: "14px",
  border: "none",
  backgroundColor: "rgba(239, 68, 68, 0.18)",
  color: "#fecaca",
  padding: "12px 14px",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer"
};

const notificationPageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const notificationTopBarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "10px"
};

const unreadBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  padding: "7px 14px",
  borderRadius: "999px",
  fontSize: "14px",
  fontWeight: 800
};

const markAllReadButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)"
};

const notificationLargeCardStyle: React.CSSProperties = {
  backgroundColor: "#f0fdfa",
  border: "1px solid #99f6e4",
  borderRadius: "18px",
  padding: "22px",
  display: "flex",
  alignItems: "center",
  gap: "18px"
};

const notificationIconBoxStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  backgroundColor: "#ccfbf1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px"
};

const notificationDateStyle: React.CSSProperties = {
  marginTop: "16px",
  color: "#64748b",
  fontSize: "13px"
};

const notificationReadCardStyle: React.CSSProperties = {
  ...notificationLargeCardStyle,
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0"
};

const markReadButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#14b8a6",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer"
};

const courseFilterTabsStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  marginBottom: "20px",
  flexWrap: "wrap"
};

const filterTabStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)"
};

const activeFilterTabStyle: React.CSSProperties = {
  ...filterTabStyle,
  backgroundColor: "#0f766e",
  color: "#ffffff"
};

const avatarImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover"
};

const chatNavItemStyle: React.CSSProperties = {
  ...navItemStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
};

const chatNavBadgeStyle: React.CSSProperties = {
  minWidth: "22px",
  height: "22px",
  padding: "0 7px",
  borderRadius: "999px",
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: 900,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

export default EmployeeDashboardPage;